import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import GlobalLoader from "../../common/GlobalLoader";
import {
  getOverallTestById,
  updateOverallTest,
} from "../../../helper/apicalls/overallTest";
import MCQViewer from "../../common/MCQViewer";
import TrueFalseViewer from "../../common/TrueFalseViewer";
import MatchViewer from "../../common/MatchViewer";
import FillUpsViewer from "../../common/FillUpsViewer";
import AnalysisComponent from "../../common/AnalysisComponent";
import { EditIcon, AddIcon } from "../../../icons";
import "./../../../styles/admin/questionBank.scss";

const OverallTestViewer = () => {
  const { setHeaderName } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditLocal, setIsEditLocal] = useState(
    (searchParams.get("mode") || "").toLowerCase() === "edit",
  );
  const buildersRef = useRef({});
  const [keySeed, setKeySeed] = useState(0);
  const [editableName, setEditableName] = useState("");

  const fetchTest = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getOverallTestById(id);
      setTest(data || null);
      const baseName = data?.name || data?.title || "";
      setEditableName(typeof baseName === "string" ? baseName : "");
    } catch (err) {
      console.error("Failed to fetch overall test by id:", err);
      setError("Unable to load overall test. Please try again.");
      setTest(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    const id = searchParams.get("id");
    if (!id || !test || !Array.isArray(test.content)) return;
    try {
      setLoading(true);
      const updatedContent = test.content.map((orig, idx) => {
        const builder = buildersRef.current[idx];
        if (typeof builder === "function") return builder(orig);
        return orig;
      });
      const trimmedName = (editableName || "").trim();
      const payload = {
        name: trimmedName || test.name || test.title || "",
        content: updatedContent,
      };
      await updateOverallTest(id, payload);
      await fetchTest(id);
      setIsEditLocal(false);
    } catch (err) {
      console.error("Failed to save overall test:", err);
      setError("Unable to save overall test. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAll = () => {
    buildersRef.current = {};
    setKeySeed((s) => s + 1);
    setIsEditLocal(false);
  };

  useEffect(() => {
    setHeaderName("Overall Test Viewer");
    const id = searchParams.get("id");
    if (id) {
      fetchTest(id);
    } else {
      setLoading(false);
      setError("No test id provided.");
    }
  }, [setHeaderName, searchParams]);

  useEffect(() => {
    if (isEditLocal) {
      setKeySeed((s) => s + 1);
    }
  }, [isEditLocal]);

  const renderContent = () => {
    if (!test || !Array.isArray(test.content) || !test.content.length) {
      return (
        <div className="overall-test-viewer-error">No questions available.</div>
      );
    }

    return (
      <div className="overall-test-question-list">
        {test.content.map((item, index) => {
          const type = item.type || "mcq";
          const questionData = item.content ?? item;

          switch (type) {
            case "mcq":
              return (
                <MCQViewer
                  key={`${keySeed}-mcq-${index}`}
                  questionData={questionData}
                  permission={isEditLocal}
                  startInEditMode={isEditLocal}
                  allowEditWithoutPersist={false}
                  provideUpdatedBuilder={(builder) => {
                    buildersRef.current[index] = builder;
                  }}
                />
              );
            case "trueorfalse":
              return (
                <TrueFalseViewer
                  key={`${keySeed}-tf-${index}`}
                  questionData={questionData}
                  permission={isEditLocal}
                  startInEditMode={isEditLocal}
                  allowEditWithoutPersist={false}
                  provideUpdatedBuilder={(builder) => {
                    buildersRef.current[index] = builder;
                  }}
                />
              );
            case "match":
              return (
                <MatchViewer
                  key={`${keySeed}-match-${index}`}
                  questionData={questionData}
                  permission={isEditLocal}
                  startInEditMode={isEditLocal}
                  provideUpdatedBuilder={(builder) => {
                    buildersRef.current[index] = builder;
                  }}
                />
              );
            case "fillups":
              return (
                <FillUpsViewer
                  key={`${keySeed}-fillups-${index}`}
                  questionData={questionData}
                  permission={isEditLocal}
                  startInEditMode={isEditLocal}
                  allowEditWithoutPersist={false}
                  provideUpdatedBuilder={(builder) => {
                    buildersRef.current[index] = builder;
                  }}
                />
              );
            case "analysis":
              return (
                <AnalysisComponent
                  key={`${keySeed}-analysis-${index}`}
                  data={questionData}
                  permission={isEditLocal}
                  startInEditMode={isEditLocal}
                  allowEditWithoutPersist={false}
                  provideUpdatedBuilder={(builder) => {
                    buildersRef.current[index] = builder;
                  }}
                />
              );
            default:
              return (
                <div key={index} className="overall-test-question">
                  <div className="overall-test-question-text">
                    Unsupported type: {type}
                  </div>
                </div>
              );
          }
        })}
      </div>
    );
  };

  return (
    <div className="overall-test-viewer">
      <GlobalLoader visible={loading} text={"Loading overall test..."} />
      {!loading && error && (
        <div className="overall-test-viewer-error">{error}</div>
      )}
      {!loading && !error && test && (
        <div>
          <div className="overall-test-header">
            {isEditLocal ? (
              <input
                type="text"
                className="overall-test-viewer-title-input"
                value={editableName}
                onChange={(e) => setEditableName(e.target.value)}
                placeholder="Enter overall test name"
              />
            ) : (
              <h2 className="overall-test-viewer-title">
                {test.name || test.title || "Overall Test"}
              </h2>
            )}
            <div className="overall-test-viewer-actions">
              {isEditLocal ? (
                <>
                  <button
                    type="button"
                    className="overall-action-btn"
                    onClick={handleCancelAll}
                  >
                    {/* <AddIcon className="action-button-icon" /> */}
                    <span>Cancel</span>
                  </button>
                  <button
                    type="button"
                    className="overall-action-btn"
                    onClick={handleSaveAll}
                  >
                    {/* <AddIcon className="action-button-icon" /> */}
                    <span>Save</span>
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="overall-action-btn"
                  onClick={() => setIsEditLocal(true)}
                >
                  <EditIcon className="action-button-icon" />
                  <span>Edit</span>
                </button>
              )}
            </div>
          </div>
          {renderContent()}
        </div>
      )}
    </div>
  );
};

export default OverallTestViewer;
